import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { FileText, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { StatusChip } from '@/components/ui/Chip';
import { ReportPreview } from '@/components/patientReports/ReportPreview';
import { ReportSectionCards } from '@/components/patientReports/ReportSectionCards';
import { useClinic } from '@/hooks/useClinicSettings';
import { loadFormsAssignmentDetail } from '@/hooks/useFormsAssignmentResponses';
import {
  downloadPatientReportPdf,
  useFinishPatientReport,
  usePatientReportList,
  usePatientReportSettings,
  usePatientReportSources,
  usePatientReportTemplates,
  useSavePatientReport,
} from '@/hooks/usePatientReports';
import { useAuth } from '@/lib/auth';
import { revealReportCpr } from '@/lib/patientReports/cpr';
import { loadReportLetterhead } from '@/lib/patientReports/letterhead';
import {
  EMPTY_REPORT_CATALOG,
  journalNoteHeading,
  type PatientReportRow,
  type ReportFieldLabels,
} from '@/lib/patientReports/model';
import { buildPatientReportPdf } from '@/lib/patientReports/pdf';
import {
  matchedQuestionnaire,
  previewToPdfBlocks,
  questionnaireAssignmentIds,
  questionnairePreviewFromDetail,
  reportSaveSelections,
  resolveLetterheadMode,
  resolveReportPreview,
  sectionsFromStored,
  selectedJournalNotes,
  selectedMarkings,
  toggleFile,
  toggleJournalNote,
  toggleMarking,
  togglePatientFields,
  upsertQuestionnaireAssignment,
  type QuestionnairePreviewDetail,
  type ReportPreviewLabels,
  type ReportSection,
} from '@/lib/patientReports/template';
import type { PatientReportLetterheadChoice } from '@/lib/patientReports/model';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function PatientReportsTab({
  clinicId,
  patientId,
  patientName: _patientName,
}: {
  clinicId: string;
  patientId: string;
  /** Kept for callers. The report title does not copy the name. */
  patientName: string;
}) {
  const { t } = useTranslation();
  const list = usePatientReportList(clinicId, patientId, true);
  const [open, setOpen] = useState<PatientReportRow | 'new' | null>(null);

  if (list.isLoading) {
    return <p className={cn(TYPE.body, 'text-ds-muted')}>{t('patientReports.loading')}</p>;
  }
  if (list.error) {
    return <ErrorAlert message={t('patientReports.load_error')} />;
  }

  if (open) {
    const initial = open === 'new' ? null : open;
    return (
      <ReportEditor
        key={initial?.id ?? 'new'}
        clinicId={clinicId}
        patientId={patientId}
        initial={initial}
        onClose={() => setOpen(null)}
      />
    );
  }

  const rows = list.data ?? [];
  return (
    <div className="flex flex-col gap-s4">
      <div className="flex flex-wrap items-center justify-between gap-s3">
        <div>
          <h2 className={cn(TYPE.heading, 'text-ds-text')}>{t('patients.tabs.patient_reports')}</h2>
          <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{t('patientReports.list_hint')}</p>
        </div>
        <Button onClick={() => setOpen('new')}>
          <Plus className="h-4 w-4" />
          {t('patientReports.new')}
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-7 w-7" />}
          title={t('patientReports.empty')}
          description={t('patientReports.empty_hint')}
        />
      ) : (
        <ul className="divide-y divide-ds-line rounded-ctl border border-ds-line bg-ds-card">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-s3 px-s4 py-s3">
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => setOpen(row)}
              >
                <span className={cn(TYPE.body, 'font-medium text-ds-text')}>{row.title}</span>
                <span className={cn(TYPE.small, 'mt-s1 block text-ds-muted')}>
                  {row.updatedAt ? new Date(row.updatedAt).toLocaleString('da-DK') : ''}
                </span>
              </button>
              <StatusChip tone={row.status === 'finished' ? 'completed' : 'neutral'}>
                {row.status === 'finished'
                  ? t('patientReports.status_finished')
                  : t('patientReports.status_draft')}
              </StatusChip>
              {row.pdfStoragePath ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    void downloadPatientReportPdf(row.pdfStoragePath!, `${row.title}.pdf`).catch(
                      () => {
                        toast.error(t('patientReports.download_error'));
                      },
                    );
                  }}
                >
                  {t('patientReports.download')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReportEditor({
  clinicId,
  patientId,
  initial,
  onClose,
}: {
  clinicId: string;
  patientId: string;
  initial: PatientReportRow | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const labels = useMemo(() => fieldLabels(t), [t]);
  const previewText = useMemo(() => previewLabels(t), [t]);
  const catalogQuery = usePatientReportSources(clinicId, patientId, true);
  const templates = usePatientReportTemplates(clinicId);
  const settings = usePatientReportSettings(clinicId);
  const clinic = useClinic(clinicId);
  const save = useSavePatientReport(clinicId, patientId);
  const finish = useFinishPatientReport(clinicId, patientId);
  const catalog = catalogQuery.data ?? EMPTY_REPORT_CATALOG;

  const [reportId, setReportId] = useState<string | null>(initial?.id ?? null);
  const [title, setTitle] = useState(initial?.title ?? defaultTitle());
  const [templateId, setTemplateId] = useState<string | null>(initial?.templateId ?? null);
  const [letterhead, setLetterhead] = useState<PatientReportLetterheadChoice>(
    initial?.letterhead ?? 'inherit',
  );
  const [includeCover, setIncludeCover] = useState(initial?.includeCover ?? false);
  const [coverTitle, setCoverTitle] = useState(initial?.coverTitle ?? '');
  const [coverBody, setCoverBody] = useState(initial?.coverBody ?? '');
  const [sections, setSections] = useState<ReportSection[]>(() =>
    sectionsFromStored(initial?.composition ?? [], initial?.blocks ?? []),
  );
  const [status, setStatus] = useState<'draft' | 'finished'>(initial?.status ?? 'draft');
  const [pdfPath, setPdfPath] = useState<string | null>(initial?.pdfStoragePath ?? null);
  const [pdfStale, setPdfStale] = useState(false);
  const [details, setDetails] = useState<Record<string, QuestionnairePreviewDetail>>({});
  const [cpr, setCpr] = useState<string | null>(null);
  const [cprAccess, setCprAccess] = useState<'hidden' | 'loading' | 'ready'>('hidden');
  const [cprError, setCprError] = useState(false);

  const markEdited = () => {
    if (status === 'finished') setPdfStale(true);
  };
  const needsCpr = sections.some(
    (section) => section.kind === 'patient_fields' && section.fields.includes('cpr'),
  );
  const canRevealCpr = hasPermission(clinicId, 'patients_cpr', 'view');
  const assignmentKey = questionnaireAssignmentIds(sections, catalog).join('|');

  useEffect(() => {
    if (!assignmentKey) return;
    let cancelled = false;
    void Promise.all(
      assignmentKey.split('|').map(async (id) => {
        const source = catalog.questionnaires.find((item) => item.id === id);
        if (!source?.formsAssignmentId) return [id, null] as const;
        try {
          const detail = await loadFormsAssignmentDetail(clinicId, source.formsAssignmentId);
          return [
            id,
            questionnairePreviewFromDetail({
              questions: detail.questionnaire?.questions ?? [],
              responses: detail.responses,
              scoreDetails: detail.score_details,
              scoringConfig: detail.questionnaire?.scoring_config ?? null,
              templateName: source.name,
              language: i18n.language,
            }),
          ] as const;
        } catch {
          return [id, null] as const;
        }
      }),
    ).then((rows) => {
      if (cancelled) return;
      const next: Record<string, QuestionnairePreviewDetail> = {};
      for (const [id, detail] of rows) {
        if (detail) next[id] = detail;
      }
      setDetails(next);
    });
    return () => {
      cancelled = true;
    };
  }, [assignmentKey, catalog.questionnaires, clinicId, i18n.language]);

  useEffect(() => {
    if (!needsCpr) {
      setCpr(null);
      setCprError(false);
      setCprAccess('ready');
      return;
    }
    if (!canRevealCpr) {
      setCpr(null);
      setCprError(false);
      setCprAccess('hidden');
      return;
    }
    let cancelled = false;
    setCprAccess('loading');
    setCprError(false);
    void revealReportCpr(patientId)
      .then((value) => {
        if (cancelled) return;
        setCpr(value);
        setCprAccess('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setCpr(null);
        setCprError(true);
        setCprAccess('ready');
      });
    return () => {
      cancelled = true;
      setCpr(null);
    };
  }, [canRevealCpr, needsCpr, patientId]);

  const questions = useMemo(() => {
    const map: Record<string, { id: string; text: string }[]> = {};
    for (const section of sections) {
      if (section.kind !== 'questionnaire') continue;
      const match = matchedQuestionnaire(section, catalog);
      const detail = match ? details[match.id] : undefined;
      if (!detail) continue;
      map[section.id] = detail.answers.map((answer) => ({ id: answer.id, text: answer.prompt }));
    }
    return map;
  }, [catalog, details, sections]);

  const resolvedLetterhead = resolveLetterheadMode(letterhead, settings.data?.letterhead);
  const preview = resolveReportPreview({
    title,
    includeCover,
    coverTitle,
    coverBody,
    letterhead,
    clinicLetterhead: settings.data?.letterhead,
    sections,
    catalog,
    cprAccess,
    cpr,
    details,
    labels: previewText,
    showEmpty: true,
  });

  const persist = async () => {
    const payload = reportSaveSelections({
      patientId,
      templateId,
      letterhead,
      includeCover,
      coverTitle,
      coverBody,
      sections,
    });
    const row = await save.mutateAsync({
      reportId,
      title: title.trim(),
      ...payload,
    });
    setReportId(row.id);
    setStatus(row.status);
    setPdfPath(row.pdfStoragePath);
    return row;
  };

  const onSave = async () => {
    if (!title.trim()) {
      toast.error(t('patientReports.title_required'));
      return;
    }
    try {
      await persist();
      toast.success(t('patientReports.saved'));
    } catch {
      toast.error(t('patientReports.save_error'));
    }
  };

  const onFinish = async () => {
    if (!title.trim()) {
      toast.error(t('patientReports.title_required'));
      return;
    }
    if (needsCpr && canRevealCpr && (cprError || cprAccess === 'loading' || !cpr)) {
      toast.error(t('patientReports.cpr_error'));
      return;
    }
    try {
      const row = await persist();
      const pdfPreview = resolveReportPreview({
        title: title.trim(),
        includeCover,
        coverTitle,
        coverBody,
        letterhead,
        clinicLetterhead: settings.data?.letterhead,
        sections,
        catalog,
        cprAccess,
        cpr,
        details,
        labels: previewText,
        showEmpty: false,
      });
      const letterheadBytes = await loadReportLetterhead(
        settings.data ?? {
          enabled: true,
          letterhead: 'none',
          letterheadStoragePath: null,
          letterheadMime: null,
        },
        clinic.data,
        letterhead,
      );
      const pdf = await buildPatientReportPdf({
        title: title.trim(),
        blocks: previewToPdfBlocks(pdfPreview),
        letterhead: letterheadBytes,
        cover: pdfPreview.cover,
      });
      const finished = await finish.mutateAsync({ reportId: row.id, pdf });
      setStatus(finished.status);
      setPdfPath(finished.pdfStoragePath);
      setPdfStale(false);
      toast.success(t('patientReports.finished'));
    } catch {
      toast.error(t('patientReports.finish_error'));
    }
  };

  const applyTemplate = (value: string) => {
    if (!value) {
      setTemplateId(null);
      markEdited();
      return;
    }
    const template = templates.data?.templates.find((item) => item.id === value);
    if (!template) return;
    if (sections.length > 0 && !window.confirm(t('patientReports.template_replace'))) return;
    setTemplateId(template.id);
    setSections(template.sections);
    setLetterhead(template.letterhead);
    setIncludeCover(template.includeCover);
    setCoverTitle(template.coverTitle);
    setCoverBody(template.coverBody);
    markEdited();
  };

  const busy = save.isPending || finish.isPending;
  const journalSection = sections.find((section) => section.kind === 'journal');
  const journalResolved =
    journalSection?.kind === 'journal'
      ? selectedJournalNotes(journalSection, catalog).map((note) => note.id)
      : [];
  const markingSection = sections.find((section) => section.kind === 'markings');
  const markingResolved =
    markingSection?.kind === 'markings'
      ? selectedMarkings(markingSection, catalog).map((marking) => marking.id)
      : [];

  return (
    <div className="flex flex-col gap-s4">
      <div className="flex flex-wrap items-end gap-s3">
        <Button variant="tertiary" onClick={onClose}>
          {t('patientReports.back')}
        </Button>
        <div className="min-w-[16rem] flex-1">
          <Input
            label={t('patientReports.title')}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              markEdited();
            }}
          />
        </div>
        <div className="min-w-[14rem]">
          <Select
            label={t('patientReports.template_label')}
            value={templateId ?? ''}
            onChange={(event) => applyTemplate(event.target.value)}
          >
            <option value="">{t('patientReports.template_blank')}</option>
            {(templates.data?.templates ?? []).map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </Select>
        </div>
        <StatusChip tone={status === 'finished' ? 'completed' : 'neutral'}>
          {status === 'finished'
            ? t('patientReports.status_finished')
            : t('patientReports.status_draft')}
        </StatusChip>
        <Button variant="secondary" loading={save.isPending} onClick={() => void onSave()}>
          {t('patientReports.save')}
        </Button>
        <Button loading={finish.isPending} onClick={() => void onFinish()}>
          {status === 'finished' ? t('patientReports.update_pdf') : t('patientReports.finish')}
        </Button>
      </div>
      <div className="flex flex-wrap items-end gap-s4">
        <div className="min-w-[14rem]">
          <Select
            label={t('patientReports.letterhead_label')}
            value={letterhead}
            onChange={(event) => {
              setLetterhead(event.target.value as PatientReportLetterheadChoice);
              markEdited();
            }}
          >
            <option value="inherit">{t('patientReports.letterhead_inherit')}</option>
            <option value="none">
              {t('settings.integrations.patient_reports.letterhead_none')}
            </option>
            <option value="invoice">
              {t('settings.integrations.patient_reports.letterhead_invoice')}
            </option>
            <option value="custom">
              {t('settings.integrations.patient_reports.letterhead_custom')}
            </option>
          </Select>
        </div>
        <label className="flex items-center gap-s2 pb-s2">
          <input
            type="checkbox"
            checked={includeCover}
            onChange={(event) => {
              setIncludeCover(event.target.checked);
              markEdited();
            }}
          />
          <span>{t('patientReports.cover')}</span>
        </label>
      </div>
      {includeCover ? (
        <div className="grid gap-s3 md:grid-cols-2">
          <Input
            label={t('patientReports.cover_title')}
            value={coverTitle}
            onChange={(event) => {
              setCoverTitle(event.target.value);
              markEdited();
            }}
          />
          <Input
            label={t('patientReports.cover_body')}
            value={coverBody}
            onChange={(event) => {
              setCoverBody(event.target.value);
              markEdited();
            }}
          />
        </div>
      ) : null}
      <p className={cn(TYPE.small, 'text-ds-muted')}>
        {t(`patientReports.letterhead_${resolvedLetterhead}`)}
        {` ${t('patientReports.override_hint')}`}
        {pdfStale ? ` ${t('patientReports.pdf_stale')}` : ''}
        {pdfPath && !pdfStale ? ` ${t('patientReports.pdf_ready')}` : ''}
      </p>

      <div className="grid items-start gap-s4 xl:grid-cols-[18rem_minmax(0,1fr)_20rem]">
        <aside className="rounded-ctl border border-ds-line bg-ds-card p-s4">
          <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('patientReports.sources')}</h3>
          <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
            {t('patientReports.sources_hint')}
          </p>
          {catalogQuery.isLoading ? (
            <p className={cn(TYPE.small, 'mt-s3 text-ds-muted')}>{t('patientReports.loading')}</p>
          ) : catalogQuery.error ? (
            <p className={cn(TYPE.small, 'mt-s3 text-ds-red-ink')}>
              {t('patientReports.sources_error')}
            </p>
          ) : (
            <div className="mt-s3 flex flex-col gap-s4">
              <SourceGroup title={t('patientReports.kind_patient_card')}>
                {catalog.patient ? (
                  <SourceCheck
                    label={catalog.patient.name || t('patientReports.kind_patient_card')}
                    checked={sections.some((section) => section.kind === 'patient_fields')}
                    disabled={busy}
                    onChange={(checked) => {
                      setSections(
                        togglePatientFields(sections, checked, t('patientReports.add_patient')),
                      );
                      markEdited();
                    }}
                  />
                ) : (
                  <p className={cn(TYPE.small, 'text-ds-muted')}>
                    {t('patientReports.none_in_group')}
                  </p>
                )}
              </SourceGroup>
              <SourceGroup title={t('patientReports.kind_journal')}>
                {catalog.journalNotes.length === 0 ? (
                  <p className={cn(TYPE.small, 'text-ds-muted')}>
                    {t('patientReports.none_in_group')}
                  </p>
                ) : (
                  catalog.journalNotes.map((note) => (
                    <SourceCheck
                      key={note.id}
                      label={journalNoteHeading(note, labels)}
                      checked={journalResolved.includes(note.id)}
                      disabled={busy}
                      onChange={(checked) => {
                        setSections(
                          toggleJournalNote(
                            sections,
                            note.id,
                            checked,
                            journalResolved,
                            t('patientReports.kind_journal'),
                          ),
                        );
                        markEdited();
                      }}
                    />
                  ))
                )}
              </SourceGroup>
              <SourceGroup title={t('patientReports.kind_marking')}>
                {catalog.markings.length === 0 ? (
                  <p className={cn(TYPE.small, 'text-ds-muted')}>
                    {t('patientReports.none_in_group')}
                  </p>
                ) : (
                  catalog.markings.map((marking) => (
                    <SourceCheck
                      key={marking.id}
                      label={marking.filename || marking.quote}
                      checked={markingResolved.includes(marking.id)}
                      disabled={busy}
                      onChange={(checked) => {
                        setSections(
                          toggleMarking(
                            sections,
                            marking.id,
                            checked,
                            markingResolved,
                            t('patientReports.kind_marking'),
                          ),
                        );
                        markEdited();
                      }}
                    />
                  ))
                )}
              </SourceGroup>
              <SourceGroup title={t('patientReports.kind_questionnaire')}>
                {catalog.questionnaires.length === 0 ? (
                  <p className={cn(TYPE.small, 'text-ds-muted')}>
                    {t('patientReports.none_in_group')}
                  </p>
                ) : (
                  catalog.questionnaires.map((form) => (
                    <SourceCheck
                      key={form.id}
                      label={form.name}
                      checked={sections.some(
                        (section) =>
                          section.kind === 'questionnaire' &&
                          (section.assignmentId === form.id ||
                            (section.assignmentId == null &&
                              section.formsTemplateId != null &&
                              section.formsTemplateId === form.formsTemplateId)),
                      )}
                      disabled={busy}
                      onChange={(checked) => {
                        setSections(
                          upsertQuestionnaireAssignment(
                            sections,
                            form,
                            checked,
                            t('patientReports.kind_questionnaire'),
                          ),
                        );
                        markEdited();
                      }}
                    />
                  ))
                )}
              </SourceGroup>
              <SourceGroup title={t('patientReports.kind_file')}>
                {catalog.files.length === 0 ? (
                  <p className={cn(TYPE.small, 'text-ds-muted')}>
                    {t('patientReports.none_in_group')}
                  </p>
                ) : (
                  catalog.files.map((file) => (
                    <SourceCheck
                      key={file.id}
                      label={file.filename}
                      checked={sections.some(
                        (section) => section.kind === 'file' && section.fileIds.includes(file.id),
                      )}
                      disabled={busy}
                      onChange={(checked) => {
                        setSections(
                          toggleFile(sections, file.id, checked, t('patientReports.kind_file')),
                        );
                        markEdited();
                      }}
                    />
                  ))
                )}
              </SourceGroup>
            </div>
          )}
        </aside>

        <ReportPreview
          model={preview}
          letterheadLabel={t(`patientReports.letterhead_${resolvedLetterhead}`)}
        />

        <aside className="rounded-ctl border border-ds-line bg-ds-sunk p-s4">
          <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('patientReports.document')}</h3>
          <p className={cn(TYPE.small, 'mb-s3 mt-s1 text-ds-muted')}>
            {t('patientReports.overview_hint')}
          </p>
          <ReportSectionCards
            sections={sections}
            tags={[...(catalog.highlightTags ?? []), ...(templates.data?.tags ?? [])].filter(
              (tag, index, all) => all.findIndex((item) => item.id === tag.id) === index,
            )}
            questions={questions}
            disabled={busy}
            onChange={(next) => {
              setSections(next);
              markEdited();
            }}
          />
        </aside>
      </div>
    </div>
  );
}

function SourceGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className={cn(TYPE.small, 'mb-s1 font-semibold text-ds-text')}>{title}</p>
      <div className="flex flex-col gap-s1">{children}</div>
    </div>
  );
}

function SourceCheck({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Checkbox
      label={<span className="line-clamp-2">{label}</span>}
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

function defaultTitle(): string {
  return `Rapport — ${new Date().toLocaleDateString('da-DK')}`;
}

function fieldLabels(t: (key: string) => string): ReportFieldLabels {
  return {
    patientCard: t('patientReports.kind_patient_card'),
    patNumber: t('patientReports.field_pat_number'),
    name: t('patientReports.field_name'),
    jobTitle: t('patientReports.field_job'),
    email: t('patientReports.field_email'),
    phone: t('patientReports.field_phone'),
    address: t('patientReports.field_address'),
    notes: t('patientReports.field_notes'),
    cave: t('patientReports.field_cave'),
    diagnosis: t('patientReports.field_diagnosis'),
    locale: t('patientReports.field_locale'),
    journal: t('patientReports.kind_journal'),
    marking: t('patientReports.kind_marking'),
    quote: t('patientReports.field_quote'),
    comment: t('patientReports.field_comment'),
    file: t('patientReports.kind_file'),
    questionnaire: t('patientReports.kind_questionnaire'),
    status: t('patientReports.field_status'),
    score: t('patientReports.field_score'),
    answer: t('patientReports.field_answer'),
    untitled: t('patientReports.untitled'),
  };
}

function previewLabels(t: (key: string) => string): ReportPreviewLabels {
  return {
    name: t('patientReports.field_name'),
    cpr: t('patientReports.field_cpr'),
    email: t('patientReports.field_email'),
    phone: t('patientReports.field_phone'),
    address: t('patientReports.field_address'),
    jobTitle: t('patientReports.field_job'),
    cave: t('patientReports.field_cave'),
    notes: t('patientReports.field_notes'),
    diagnosis: t('patientReports.field_diagnosis'),
    patNumber: t('patientReports.field_pat_number'),
    locale: t('patientReports.field_locale'),
    notRecorded: t('patientReports.not_recorded'),
    cprHidden: t('patientReports.cpr_hidden'),
    cprLoading: t('patientReports.cpr_loading'),
    comment: t('patientReports.field_comment'),
    file: t('patientReports.kind_file'),
    page: t('patientReports.page'),
    quote: t('patientReports.field_quote'),
    score: t('patientReports.field_score'),
    answer: t('patientReports.field_answer'),
    missingQuestionnaire: t('patientReports.missing_questionnaire'),
  };
}
